import { useTranslation } from 'react-i18next';
import { Text, TextInput } from '@/shared/ui';
import type { ProposedAppointment } from '../types/vistory.types';

interface EditableProps {
  title: string;
  setTitle: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  t: (k: string) => string;
}

/**
 * Editable title/description fields shown while a vistory is a draft.
 *
 * @param {EditableProps} props - Field values, setters, and translator.
 * @returns {JSX.Element} Rendered editable fields.
 */
export function EditableFields({ title, setTitle, description, setDescription, t }: EditableProps) {
  return (
    <>
      <TextInput
        label={t('vistory.field_title')}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <TextInput
        asTextArea
        label={t('vistory.field_description')}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
    </>
  );
}

/**
 * Read-only title/description shown once a vistory is no longer a draft.
 *
 * @param {{ title: string; description: string }} props - Field values.
 * @returns {JSX.Element} Rendered read-only fields.
 */
export function ReadOnlyFields({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col gap-[var(--space-2)]">
      <Text size="sm" fw={500} className="text-[var(--text-primary)]">{title}</Text>
      {description && <Text size="sm" className="text-[var(--text-secondary)]">{description}</Text>}
    </div>
  );
}

/**
 * Notice block that surfaces the reviewer's rejection reason and proposed appointments.
 */
export function RejectionNotice({ reason, label, appointments }: {
  reason: string;
  label: string;
  appointments?: ProposedAppointment[];
}) {
  const { t } = useTranslation('features/vistories');
  return (
    <div className="rounded-(--radius-md) border border-[var(--status-error)] bg-[var(--status-error-subtle)] p-[var(--space-3)]">
      <Text size="xs" fw={500} className="text-[var(--status-error)]">{label}</Text>
      <Text size="sm" className="text-[var(--text-primary)]">{reason}</Text>
      {appointments && appointments.length > 0 && (
        <div className="mt-[var(--space-2)] flex flex-col gap-[var(--space-1)]">
          <Text size="xs" fw={500} className="text-[var(--text-secondary)]">
            {t('vistory.proposed_appointments')}
          </Text>
          {appointments.map((appt, i) => (
            <Text key={i} size="xs" className="text-[var(--text-primary)]">
              {appt.date} {appt.time}{appt.note ? ` · ${appt.note}` : ''}
            </Text>
          ))}
        </div>
      )}
    </div>
  );
}
